import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createAdminReadingListEntry,
  deleteAdminReadingListEntry,
  getAdminReadingList,
  updateAdminReadingListEntry,
} from '../../api/siteData';
import { renderMarkdownToHtml } from '../../utils/markdown';
import {
  AdminCommandPalette,
  AdminMcpConfigButton,
  AdminWorkspaceSwitch,
  createSearchText,
  createTagSearchParts,
  matchesSearchQuery,
  useAdminKeyboardShortcuts,
} from './adminControls';
import {
  getBookSummary,
  groupBooksByYear,
  hasBookSummary,
  slugifyReadingListValue,
} from '../books/readingList';

function getDefaultYear() {
  return String(new Date().getFullYear());
}

function getDefaultNewBookDraft() {
  return {
    year: getDefaultYear(),
    title: '',
    author: '',
    slug: '',
    relatedPostSlug: '',
    relatedPostLabel: '',
    finishedOn: '',
    score: '',
  };
}

function getAutoGeneratedSlug(title) {
  return slugifyReadingListValue(title);
}

function shouldAutoSyncSlug(currentSlug, currentTitle) {
  const normalizedCurrentSlug = String(currentSlug || '').trim();
  return !normalizedCurrentSlug || normalizedCurrentSlug === getAutoGeneratedSlug(currentTitle);
}

function normalizeBlogLinkInput(value) {
  const trimmedValue = String(value || '').trim();

  if (!trimmedValue) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  const blogPathMatch = trimmedValue.match(/^\/?blog\/([^/?#]+)/i);

  if (!blogPathMatch) {
    return trimmedValue;
  }

  try {
    return decodeURIComponent(blogPathMatch[1]);
  } catch (error) {
    return blogPathMatch[1];
  }
}

function formatBookScore(score) {
  if (score === undefined || score === null || score === '') {
    return null;
  }

  const numericScore = Number(score);

  if (!Number.isFinite(numericScore)) {
    return null;
  }

  return numericScore % 1 === 0 ? `${numericScore.toFixed(0)}/5` : `${numericScore.toFixed(1)}/5`;
}

function formatBookMeta(book) {
  const parts = [book.author];

  if (hasBookSummary(book)) {
    parts.push('Summary');
  }

  if (book.relatedPostSlug) {
    parts.push('Blog link');
  }

  if (book.finishedOn) {
    parts.push(`Finished ${book.finishedOn}`);
  }

  const formattedScore = formatBookScore(book.score);

  if (formattedScore) {
    parts.push(formattedScore);
  }

  parts.push(`Order ${book.sortOrder}`);
  return parts.join(' · ');
}

function getNextSortOrder(books, year) {
  const targetYear = Number(year);

  if (!Number.isFinite(targetYear)) {
    return 0;
  }

  const maxSortOrder = books.reduce((currentMax, book) => {
    if (Number(book.year) !== targetYear) {
      return currentMax;
    }

    const sortOrder = Number(book.sortOrder);
    return Number.isFinite(sortOrder) ? Math.max(currentMax, sortOrder) : currentMax;
  }, -1);

  return maxSortOrder + 1;
}

function ReadingListWorkspace({
  adminKeyword,
  activeWorkspace,
  onLogout,
  onWorkspaceChange,
}) {
  const [books, setBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [newBookDraft, setNewBookDraft] = useState(getDefaultNewBookDraft);
  const [isLoadingBooks, setIsLoadingBooks] = useState(false);
  const [workspaceError, setWorkspaceError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [sidebarSearch, setSidebarSearch] = useState('');
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const newBookTitleInputRef = useRef(null);

  const selectedBook = useMemo(
    () => books.find((book) => book.id === selectedBookId) || null,
    [books, selectedBookId],
  );
  const filteredBooks = useMemo(() => {
    if (!sidebarSearch.trim()) return books;

    return books.filter((book) => matchesSearchQuery(createSearchText([
      book.year,
      book.title,
      book.author,
      book.slug,
      book.summaryMarkdown,
      book.relatedPostSlug,
      book.relatedPostLabel,
      book.finishedOn,
      book.score,
      createTagSearchParts(book.tags),
    ]), sidebarSearch));
  }, [books, sidebarSearch]);
  const booksByYear = useMemo(() => groupBooksByYear(filteredBooks), [filteredBooks]);

  async function loadBooks(keyword = adminKeyword, preferredBookId = selectedBookId) {
    if (!keyword) {
      return false;
    }

    setIsLoadingBooks(true);
    setWorkspaceError('');

    try {
      const nextBooks = await getAdminReadingList(keyword);
      setBooks(nextBooks);
      setSelectedBookId((currentBookId) => {
        const candidateId = preferredBookId || currentBookId;

        if (candidateId && nextBooks.some((book) => book.id === candidateId)) {
          return candidateId;
        }

        return nextBooks[0]?.id || null;
      });
      return true;
    } catch (error) {
      setWorkspaceError(error.message);
      return false;
    } finally {
      setIsLoadingBooks(false);
    }
  }

  useEffect(() => {
    loadBooks(adminKeyword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKeyword]);

  useAdminKeyboardShortcuts({
    isCommandPaletteOpen,
    onOpenCommandPalette: () => setIsCommandPaletteOpen(true),
    onCloseCommandPalette: () => setIsCommandPaletteOpen(false),
    onSave: handleSaveBook,
    onClose: () => {
      if (!sidebarSearch) {
        return false;
      }

      setSidebarSearch('');
      return true;
    },
  });

  const commandPaletteCommands = [
    {
      id: 'save-selected-book',
      label: 'Save selected book',
      detail: selectedBook ? selectedBook.title : 'No book selected',
      shortcut: '⌘↵',
      disabled: !selectedBook,
      action: handleSaveBook,
    },
    {
      id: 'refresh-reading-list',
      label: 'Refresh reading list',
      detail: 'Reload books from the server',
      action: () => loadBooks(adminKeyword, selectedBookId),
    },
    {
      id: 'focus-new-book',
      label: 'New book title',
      detail: 'Move to the create form',
      action: () => {
        newBookTitleInputRef.current?.focus();
      },
    },
    {
      id: 'clear-reading-search',
      label: 'Clear search',
      detail: sidebarSearch ? sidebarSearch : 'Search is empty',
      shortcut: 'Esc',
      disabled: !sidebarSearch,
      action: () => setSidebarSearch(''),
    },
    {
      id: 'open-bookshelf',
      label: 'Open Bookshelf',
      detail: 'Switch admin workspace',
      action: () => onWorkspaceChange('bookshelf'),
    },
    {
      id: 'logout-admin',
      label: 'Logout',
      detail: 'End the admin session',
      action: onLogout,
    },
  ];

  function handleBookFieldChange(fieldName, value) {
    setBooks((currentBooks) => currentBooks.map((book) => (
      book.id === selectedBookId
        ? (() => {
          if (fieldName === 'title') {
            const nextSlug = shouldAutoSyncSlug(book.slug, book.title)
              ? getAutoGeneratedSlug(value)
              : book.slug;

            return {
              ...book,
              title: value,
              slug: nextSlug,
            };
          }

          return {
            ...book,
            [fieldName]: value,
          };
        })()
        : book
    )));
  }

  async function handleCreateBook() {
    const trimmedTitle = newBookDraft.title.trim();
    const trimmedAuthor = newBookDraft.author.trim();
    const trimmedSlug = String(newBookDraft.slug || '').trim();

    if (!trimmedTitle || !trimmedAuthor || !String(newBookDraft.year || '').trim() || !trimmedSlug) {
      setWorkspaceError('Year, title, author, and slug are required.');
      return;
    }

    setWorkspaceError('');
    setStatusMessage('');

    try {
      const trimmedFinishedOn = String(newBookDraft.finishedOn || '').trim();
      const trimmedScore = String(newBookDraft.score || '').trim();
      const relatedPostSlug = normalizeBlogLinkInput(newBookDraft.relatedPostSlug);
      const relatedPostLabel = String(newBookDraft.relatedPostLabel || '').trim();
      const payload = await createAdminReadingListEntry(adminKeyword, {
        year: newBookDraft.year,
        title: trimmedTitle,
        author: trimmedAuthor,
        slug: trimmedSlug,
        sortOrder: getNextSortOrder(books, newBookDraft.year),
        ...(relatedPostSlug ? { relatedPostSlug } : {}),
        ...(relatedPostSlug && relatedPostLabel ? { relatedPostLabel } : {}),
        ...(trimmedFinishedOn ? { finishedOn: trimmedFinishedOn } : {}),
        ...(trimmedScore ? { score: trimmedScore } : {}),
      });

      const nextBook = payload.book;
      await loadBooks(adminKeyword, nextBook.id);
      setNewBookDraft(getDefaultNewBookDraft());
      setStatusMessage('Book added.');
    } catch (error) {
      setWorkspaceError(error.message);
    }
  }

  async function handleSaveBook() {
    if (!selectedBook) {
      return;
    }

    const trimmedTitle = String(selectedBook.title || '').trim();
    const trimmedAuthor = String(selectedBook.author || '').trim();
    const trimmedSlug = String(selectedBook.slug || '').trim();

    if (!trimmedTitle || !trimmedAuthor || !String(selectedBook.year || '').trim() || !trimmedSlug) {
      setWorkspaceError('Year, title, author, and slug are required.');
      return;
    }

    setWorkspaceError('');
    setStatusMessage('');

    try {
      const relatedPostSlug = normalizeBlogLinkInput(selectedBook.relatedPostSlug);
      const relatedPostLabel = String(selectedBook.relatedPostLabel || '').trim();
      const payload = await updateAdminReadingListEntry(adminKeyword, selectedBook.id, {
        year: selectedBook.year,
        title: trimmedTitle,
        author: trimmedAuthor,
        sortOrder: selectedBook.sortOrder,
        slug: trimmedSlug,
        summaryMarkdown: selectedBook.summaryMarkdown,
        relatedPostSlug,
        relatedPostLabel: relatedPostSlug ? relatedPostLabel || null : null,
        finishedOn: String(selectedBook.finishedOn || '').trim() || null,
        score: String(selectedBook.score ?? '').trim() || null,
      });

      await loadBooks(adminKeyword, payload.book.id);
      setStatusMessage('Book saved.');
    } catch (error) {
      setWorkspaceError(error.message);
    }
  }

  async function handleDeleteBook() {
    if (!selectedBook) {
      return;
    }

    if (typeof window !== 'undefined' && !window.confirm(`Delete "${selectedBook.title}"?`)) {
      return;
    }

    setWorkspaceError('');
    setStatusMessage('');

    try {
      await deleteAdminReadingListEntry(adminKeyword, selectedBook.id);
      const remainingBookIds = books.filter((book) => book.id !== selectedBook.id).map((book) => book.id);
      await loadBooks(adminKeyword, remainingBookIds[0] || null);
      setStatusMessage('Book deleted.');
    } catch (error) {
      setWorkspaceError(error.message);
    }
  }

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div>
            <p className="admin-sidebar-label">Admin</p>
            <h1>Reading list</h1>
          </div>
          <div className="admin-top-actions">
            <button
              className="admin-command-trigger"
              type="button"
              onClick={() => setIsCommandPaletteOpen(true)}
              aria-label="Open admin search"
            >
              ⌘K
            </button>
            <AdminMcpConfigButton adminKeyword={adminKeyword} />
            <button
              className="admin-ghost-button"
              type="button"
              onClick={onLogout}
            >
              Logout
            </button>
          </div>
        </div>

        <AdminWorkspaceSwitch
          activeWorkspace={activeWorkspace}
          onWorkspaceChange={onWorkspaceChange}
        />

        <section className="admin-sidebar-panel">
          <div>
            <p className="admin-sidebar-label">Create book</p>
            <p className="admin-side-note">Add a new reading-list entry, then refine the details on the right.</p>
          </div>

          <div className="admin-field-grid">
            <label className="admin-field admin-field-compact">
              <span>Year</span>
              <input
                type="number"
                value={newBookDraft.year}
                onChange={(event) => setNewBookDraft((currentDraft) => ({
                  ...currentDraft,
                  year: event.target.value,
                }))}
              />
            </label>

            <label className="admin-field">
              <span>Title</span>
              <input
                ref={newBookTitleInputRef}
                type="text"
                value={newBookDraft.title}
                onChange={(event) => setNewBookDraft((currentDraft) => ({
                  ...currentDraft,
                  title: event.target.value,
                  slug: shouldAutoSyncSlug(currentDraft.slug, currentDraft.title)
                    ? getAutoGeneratedSlug(event.target.value)
                    : currentDraft.slug,
                }))}
                placeholder="The Art of Learning"
              />
            </label>

            <label className="admin-field">
              <span>Author</span>
              <input
                type="text"
                value={newBookDraft.author}
                onChange={(event) => setNewBookDraft((currentDraft) => ({
                  ...currentDraft,
                  author: event.target.value,
                }))}
                placeholder="Josh Waitzkin"
              />
            </label>

            <label className="admin-field">
              <span>Slug</span>
              <input
                type="text"
                value={newBookDraft.slug}
                onChange={(event) => setNewBookDraft((currentDraft) => ({
                  ...currentDraft,
                  slug: event.target.value,
                }))}
                placeholder="shareable-book-slug"
              />
            </label>

            <label className="admin-field">
              <span>Blog post link</span>
              <input
                type="text"
                value={newBookDraft.relatedPostSlug}
                onChange={(event) => setNewBookDraft((currentDraft) => ({
                  ...currentDraft,
                  relatedPostSlug: event.target.value,
                }))}
                placeholder="about-nvidia-way or https://domelian.substack.com/p/..."
              />
            </label>

            <label className="admin-field">
              <span>Blog link label</span>
              <input
                type="text"
                value={newBookDraft.relatedPostLabel}
                onChange={(event) => setNewBookDraft((currentDraft) => ({
                  ...currentDraft,
                  relatedPostLabel: event.target.value,
                }))}
                placeholder="blog post"
              />
            </label>

            <label className="admin-field admin-field-compact">
              <span>Finished on</span>
              <input
                type="date"
                value={newBookDraft.finishedOn}
                onChange={(event) => setNewBookDraft((currentDraft) => ({
                  ...currentDraft,
                  finishedOn: event.target.value,
                }))}
              />
            </label>

            <label className="admin-field admin-field-compact">
              <span>Score</span>
              <input
                type="text"
                value={newBookDraft.score}
                onChange={(event) => setNewBookDraft((currentDraft) => ({
                  ...currentDraft,
                  score: event.target.value,
                }))}
                placeholder="3.5 or 3,5/5"
              />
            </label>
          </div>

          <button
            className="admin-solid-button"
            type="button"
            onClick={handleCreateBook}
          >
            Add
          </button>
        </section>

      </aside>

      <main className="admin-workspace">
        <div className="admin-workspace-header">
          <div>
            <p className="admin-sidebar-label">Selected book</p>
            <h2>{selectedBook ? selectedBook.title : 'Select a book'}</h2>
            {selectedBook && (
              <p className="admin-side-note">{formatBookMeta(selectedBook)}</p>
            )}
          </div>
          <div className="admin-top-actions">
            <button
              className="admin-ghost-button"
              type="button"
              onClick={() => loadBooks(adminKeyword, selectedBookId)}
            >
              Refresh
            </button>
            {selectedBook && (
              <>
                <button
                  className="admin-ghost-button"
                  type="button"
                  onClick={handleDeleteBook}
                >
                  Delete
                </button>
                <button
                  className="admin-solid-button"
                  type="button"
                  onClick={handleSaveBook}
                >
                  Save
                </button>
              </>
            )}
          </div>
        </div>

        {(workspaceError || statusMessage) && (
          <div className="admin-feedback">
            {workspaceError && <p className="admin-feedback-error">{workspaceError}</p>}
            {!workspaceError && statusMessage && <p className="admin-feedback-success">{statusMessage}</p>}
          </div>
        )}

        {!selectedBook && (
          <section className="admin-empty-state">
            <p>Select a book on the left or add a new one.</p>
          </section>
        )}

        {selectedBook && (
          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-header">
              <div>
                <h3>Book details</h3>
                <p className="admin-side-note">Summary supports Markdown. Slug is required so each note can be shared directly.</p>
              </div>
            </div>

            <div className="admin-note-grid">
              <div className="admin-note-editor-pane">
                <div className="admin-inline-fields">
                  <label className="admin-field admin-field-compact">
                    <span>Year</span>
                    <input
                      type="number"
                      value={selectedBook.year}
                      onChange={(event) => handleBookFieldChange('year', event.target.value)}
                    />
                  </label>

                  <label className="admin-field admin-field-compact">
                    <span>Sort order</span>
                    <input
                      type="number"
                      value={selectedBook.sortOrder}
                      onChange={(event) => handleBookFieldChange('sortOrder', event.target.value)}
                    />
                  </label>

                  <label className="admin-field admin-field-compact">
                    <span>Finished on</span>
                    <input
                      type="date"
                      value={selectedBook.finishedOn || ''}
                      onChange={(event) => handleBookFieldChange('finishedOn', event.target.value)}
                    />
                  </label>

                  <label className="admin-field admin-field-compact">
                    <span>Score</span>
                    <input
                      type="text"
                      value={selectedBook.score ?? ''}
                      onChange={(event) => handleBookFieldChange('score', event.target.value)}
                      placeholder="3.5 or 3,5/5"
                    />
                  </label>
                </div>

                <label className="admin-field">
                  <span>Title</span>
                  <input
                    type="text"
                    value={selectedBook.title || ''}
                    onChange={(event) => handleBookFieldChange('title', event.target.value)}
                  />
                </label>

                <label className="admin-field">
                  <span>Author</span>
                  <input
                    type="text"
                    value={selectedBook.author || ''}
                    onChange={(event) => handleBookFieldChange('author', event.target.value)}
                  />
                </label>

                <label className="admin-field">
                  <span>Slug</span>
                  <input
                    type="text"
                    value={selectedBook.slug || ''}
                    onChange={(event) => handleBookFieldChange('slug', event.target.value)}
                    placeholder="shareable-book-slug"
                  />
                </label>

                <div className="admin-inline-fields admin-inline-fields-stacked">
                  <label className="admin-field">
                    <span>Blog post link</span>
                    <input
                      type="text"
                      value={selectedBook.relatedPostSlug || ''}
                      onChange={(event) => handleBookFieldChange('relatedPostSlug', event.target.value)}
                      placeholder="about-nvidia-way or https://domelian.substack.com/p/..."
                    />
                  </label>

                  <label className="admin-field">
                    <span>Blog link label</span>
                    <input
                      type="text"
                      value={selectedBook.relatedPostLabel || ''}
                      onChange={(event) => handleBookFieldChange('relatedPostLabel', event.target.value)}
                      placeholder="blog post"
                    />
                  </label>
                </div>

                <label className="admin-field">
                  <span>Summary Markdown</span>
                  <textarea
                    rows="14"
                    value={selectedBook.summaryMarkdown || ''}
                    onChange={(event) => handleBookFieldChange('summaryMarkdown', event.target.value)}
                    placeholder="Optional notes, quotes, and takeaways."
                  />
                </label>
              </div>

              <div className="admin-note-preview-pane">
                <div className="admin-book-preview-card">
                  <p className="admin-log-label">Preview</p>
                  <h3 className="admin-book-preview-title">{selectedBook.title || 'Untitled book'}</h3>
                  <p className="admin-book-preview-author">{selectedBook.author || 'Author missing'}</p>
                  <p className="admin-side-note">{formatBookMeta(selectedBook)}</p>
                  <div
                    className="admin-preview admin-preview-note"
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdownToHtml(getBookSummary(selectedBook) || 'No summary yet.'),
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <section className="admin-bottom-panel">
        <div className="admin-bottom-panel-header">
          <p className="admin-sidebar-label">Books</p>
          <div className="admin-sidebar-search">
            <input
              type="text"
              value={sidebarSearch}
              onChange={(event) => setSidebarSearch(event.target.value)}
              placeholder="Search words, authors, notes..."
              aria-label="Search reading list"
            />
            {sidebarSearch && (
              <span className="admin-sidebar-search-count">
                {filteredBooks.length} result{filteredBooks.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        <div className="admin-bottom-panel-grid">
          {booksByYear.map(({ year, books: yearBooks }) => (
            <section className="admin-reading-year-group" key={year}>
              <p className="admin-reading-year-heading">{year}</p>
              <div className="admin-question-list admin-question-list-compact">
                {yearBooks.map((book) => (
                  <button
                    key={book.id}
                    className={`admin-question-item${book.id === selectedBookId ? ' is-active' : ''}`}
                    type="button"
                    onClick={() => setSelectedBookId(book.id)}
                  >
                    <span className="admin-question-row">
                      <span className="admin-question-title">{book.title}</span>
                      {book.id === selectedBookId && (
                        <span className="admin-question-badge">Selected</span>
                      )}
                    </span>
                    <span className="admin-question-meta">{formatBookMeta(book)}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}

          {isLoadingBooks && <p className="admin-side-note">Loading books...</p>}
          {!isLoadingBooks && books.length === 0 && <p className="admin-side-note">No books yet.</p>}
        </div>
      </section>

      <AdminCommandPalette
        commands={commandPaletteCommands}
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        onSearchChange={setSidebarSearch}
        searchPlaceholder="Search books or run a command..."
        searchResultLabel={`${filteredBooks.length} matching book${filteredBooks.length !== 1 ? 's' : ''}`}
        searchValue={sidebarSearch}
      />
    </div>
  );
}

export default ReadingListWorkspace;
